import { Dispatch, SetStateAction, useEffect } from 'react';
import cn from 'classnames';
import Link from 'next/link';
import { ArrowUpLeft, SearchIcon } from 'lucide-react';
import Loading from '@/components/Loading/Loading';
import { useSearchSuggestions } from '@/lib/useSearchSuggestions';
import type { SearchProductsResponseBody } from '@/api/catalog-v2/search/products';
import { trackProductsSearched } from '@/lib/analytics/trackProduct';
import PopularSearches from '@/components/commons/Navbar/ExtendedNavbar/Search/Mobile/PopularSearches';
import i18n from '@/settings/i18n';

const t = i18n.init('shared');

export default function SearchOutput({
  search,
  setResults,
  onSelect,
  onAutofill,
}: {
  search: string;
  setResults: Dispatch<SetStateAction<SearchProductsResponseBody | null>>;
  onSelect: () => void;
  onAutofill: (value: string) => void;
}) {
  const { data, error } = useSearchSuggestions(search ? { q: search } : null);
  const isLoading = !data && !error && search.length > 0;
  const items = data?.items ?? [];
  const trimmedSearch = search.trim();

  useEffect(() => {
    if (data != null) {
      setResults(data);
    }
  }, [data, setResults]);

  const trackSearchSubmitted = () => {
    if (search !== '') {
      trackProductsSearched(search);
    }
  };

  return (
    <div className="mt-2 w-full overflow-hidden rounded-2xl border border-gray-200 bg-white py-4 shadow-xl">
      {isLoading && (
        <div className="flex items-center justify-center py-8">
          <Loading fill="none" />
        </div>
      )}

      {!isLoading && trimmedSearch !== '' && (
        <section>
          <p className="px-4 text-sm font-bold leading-normal text-gray-700">
            {t`Suggestions`}
          </p>
          <ul className="mt-1">
            {items.map((item) => (
              <li
                key={item.id}
                className="flex items-center border-b border-gray-200 px-4 hover:bg-gray-200"
              >
                <Link
                  href={`/collections/${item.slug}`}
                  onClick={() => {
                    trackSearchSubmitted();
                    onSelect();
                  }}
                  className="flex min-w-0 flex-1 cursor-pointer items-center py-3"
                >
                  <span className="truncate text-[13px] font-medium leading-normal">
                    {item.name}
                  </span>
                </Link>
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onAutofill(item.name);
                  }}
                  aria-label={`Fill search with ${item.name}`}
                  className="flex shrink-0 items-center justify-center p-2 text-gray-700"
                >
                  <ArrowUpLeft className="h-4 w-4" />
                </button>
              </li>
            ))}

            <li className="px-4 hover:bg-gray-200">
              <Link
                href={`/search?query=${encodeURIComponent(search)}`}
                onClick={() => {
                  trackSearchSubmitted();
                  onSelect();
                }}
                className="flex cursor-pointer items-center justify-between py-3"
              >
                <span className="truncate text-[13px] font-medium leading-normal">
                  &ldquo;{search}&rdquo;
                </span>
                <SearchIcon className="h-4 w-4 shrink-0 text-gray-700" />
              </Link>
            </li>
          </ul>
        </section>
      )}

      {!isLoading && (
        <div className={cn('px-4', trimmedSearch !== '' && 'mt-4')}>
          <PopularSearches variant="desktop" onSelect={onSelect} />
        </div>
      )}
    </div>
  );
}
